import type { BlogPost } from '../types';

export const post: BlogPost = {
  "id": "462",
  "slug": "what-is-a-glacier-how-glaciers-form",
  "title": "What Is a Glacier? How Glaciers Form and Shape the Land",
  "excerpt": "A glacier is a slow-moving river of ice and one of the most powerful sculptors on Earth. Here is how glaciers form, how they move, and the landscapes they leave behind.",
  "date": "2026-06-17",
  "category": "Geography",
  "image": "https://images.unsplash.com/photo-1678972993184-5179b3b2525c?ixid=M3w5NjY4NzN8MHwxfHNlYXJjaHwxfHxnbGFjaWVyJTIwaWNlJTIwdmFsbGV5fGVufDF8MHx8fDE3ODE2ODQyMjJ8MA&ixlib=rb-4.1.0&w=800&q=80&fit=crop",
  "readTime": "4 min",
  "author": "EarthGuessr Team",
  "tldr": "A glacier is a large, lasting mass of ice that builds up where more snow falls each year than melts away. Under its own weight the packed snow turns to ice and slowly flows downhill, carving valleys, fjords, and lakes as it goes.",
  "content": [
    {
      "type": "paragraph",
      "text": "Ask people which landform most fascinates them and a surprising number say fjords. The reason those steep, water-filled valleys exist comes down to a single, patient force: glaciers. Long before a fjord fills with seawater, a glacier spent thousands of years grinding the rock into shape. Understanding glaciers is the key to understanding a huge share of the scenery you see from the air, from the mountains of Norway to the lake country of Canada."
    },
    {
      "type": "heading",
      "text": "What Exactly Is a Glacier?"
    },
    {
      "type": "paragraph",
      "text": "A glacier is a thick, persistent mass of ice that forms on land and moves under its own weight. The word persistent matters: a snowbank that melts every summer is not a glacier. A glacier survives year after year because, over the long run, more snow accumulates than disappears. That balance lets ice pile up to depths of tens or even hundreds of meters, until gravity finally sets the whole mass in motion."
    },
    {
      "type": "paragraph",
      "text": "Glaciers come in two broad families. Valley glaciers, also called alpine glaciers, flow down mountain valleys like frozen rivers. Ice sheets and ice caps are vast domes of ice that bury entire landscapes. Antarctica and Greenland hold the two great ice sheets on Earth today, and together they store the overwhelming majority of the planet's fresh water, locked up as ice."
    },
    {
      "type": "heading",
      "text": "How Glaciers Form"
    },
    {
      "type": "paragraph",
      "text": "A glacier is essentially compressed snow with a very long memory. The process unfolds in stages:"
    },
    {
      "type": "list",
      "items": [
        "Snow accumulates in a place cold enough that it does not fully melt each summer.",
        "Each new snowfall presses down on the layers beneath it, squeezing out the trapped air.",
        "The buried snow recrystallizes into a grainy, dense form called firn.",
        "Under still more pressure, the firn fuses into solid glacial ice, often with a faint blue tint because dense ice absorbs red light.",
        "Once the ice is thick enough, usually tens of meters, it begins to deform and flow downhill."
      ]
    },
    {
      "type": "paragraph",
      "text": "This is why glaciers are sometimes described as rivers of ice. They are not static blocks; they are always on the move, even when the change is far too slow for the eye to follow. The upper part, where snow piles up, is called the accumulation zone, while the lower part, where ice melts away, is the ablation zone. The balance between the two decides whether a glacier is advancing or retreating."
    },
    {
      "type": "heading",
      "text": "How Glaciers Move"
    },
    {
      "type": "paragraph",
      "text": "Glaciers travel in two ways at once. The first is internal deformation: under enormous pressure, ice crystals slide past one another so the whole mass creeps forward like extremely stiff honey. The second is basal sliding, where a thin film of meltwater at the base lets the glacier slip over the bedrock. Most glaciers move only a few centimeters to a few meters a day, though some surging glaciers can lurch forward far faster for short bursts."
    },
    {
      "type": "paragraph",
      "text": "As a glacier flows, it acts like a conveyor belt loaded with sandpaper. Rocks frozen into its underside scrape and pluck at the bedrock, while the sheer weight of the ice bulldozes anything in its path. Given enough time, that grinding rebuilds the entire shape of the land beneath and around it."
    },
    {
      "type": "heading",
      "text": "The Landscapes Glaciers Leave Behind"
    },
    {
      "type": "paragraph",
      "text": "Once you learn the signatures of glacial erosion, you start to see them everywhere. Classic glacial landforms include:"
    },
    {
      "type": "list",
      "items": [
        "U-shaped valleys, with broad floors and steep walls, carved by the wide tongue of a valley glacier.",
        "Fjords, which are simply U-shaped glacial valleys later drowned by the sea.",
        "Cirques, the bowl-shaped hollows high on a mountainside where a glacier first took hold.",
        "Moraines, ridges of rock and grit bulldozed into long mounds along a glacier's edges and snout.",
        "Glacial lakes, including the long ribbon lakes and countless small tarns that fill scoured-out hollows.",
        "Erratics, large boulders carried far from their origin and dumped where the ice finally melted."
      ]
    },
    {
      "type": "paragraph",
      "text": "Whole regions owe their character to ice. The Great Lakes of North America, the fjords of Scandinavia, and the lake-dotted landscapes of Finland and Canada were all shaped during past glaciations. Read the land carefully and you are reading the history of the ice ages."
    },
    {
      "type": "heading",
      "text": "Where to Find Glaciers Today"
    },
    {
      "type": "paragraph",
      "text": "Most of the planet's glacier ice sits in Antarctica and Greenland, but glaciers cling to high mountains on every continent except Australia. The Alps, the Himalayas, the Andes, Alaska, Patagonia, and the mountains of New Zealand all carry them. Many are retreating as the climate warms, which makes the fresh rock and rubble they leave behind easier to read with each passing decade, and turns glaciers into some of the most closely watched features on Earth."
    },
    {
      "type": "heading",
      "text": "Spotting Glaciers from Above"
    },
    {
      "type": "paragraph",
      "text": "Look for ice following a valley, with darker debris bands along its edges or surface. Distinguish visible ice from evidence of past glaciation: a fjord or a glacial valley may remain after the glacier has retreated."
    }
  ]
};
